import { createContext, useContext, useState, useEffect, ReactNode } from "react";
import enTranslations from "../translations/en.json";
import arTranslations from "../translations/ar.json";
import enProjectSale from "../translations/en-project-sale.json";
import arProjectSale from "../translations/ar-project-sale.json";
import enGeneral from "../translations/en-general.json";
import arGeneral from "../translations/ar-general.json";

type Language = "ar" | "en";

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  toggleLanguage: () => void;
  t: (key: string) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

// Merge all translation files
const mergeTranslations = (main: any, projectSale: any, general: any) => {
  return {
    ...main,
    ...projectSale,
    ...general
  };
};

// Translation dictionary
const translations = {
  ar: mergeTranslations(arTranslations, arProjectSale, arGeneral),
  en: mergeTranslations(enTranslations, enProjectSale, enGeneral)
};

export const LanguageProvider = ({ children }: { children: ReactNode }) => {
  const [language, setLanguage] = useState<Language>("ar");

  useEffect(() => {
    // Check if language is already set in localStorage
    const storedLang = localStorage.getItem("language");
    if (storedLang && (storedLang === "ar" || storedLang === "en")) {
      setLanguage(storedLang);
    }
  }, []);

  const toggleLanguage = () => {
    const newLang = language === "ar" ? "en" : "ar";
    setLanguage(newLang);
    localStorage.setItem("language", newLang);
  };

  const t = (key: string): string => {
    return key.split('.').reduce((obj: any, k: string) => obj?.[k], translations[language]) || key;
  };

  const value = {
    language,
    setLanguage,
    toggleLanguage,
    t,
  };

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
};